import EnvConfig from '../Config/env.config';
import { isEqual } from 'lodash-es';
import { SegmentName } from '~/Constants/Segment.constants';

// after changing this flag, the page needs to be refreshed manually
const ENABLE_DEBUGGING = false;

class SegmentTracking {
  private lastGroupId: string = '';
  private lastUserId: string = '';
  private lastUserTraits: Record<string, string> = {};

  constructor(API_KEY: string) {
    this.init(API_KEY);
  }

  init = (API_KEY: string) => {
    // Official non-minified Segment snippet, copied from here and adapted to Neo's Typescript and ESLint rules:
    // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/quickstart/#step-2-copy-the-segment-snippet

    if (!API_KEY) {
      throw new Error('Missing Segment API key');
    }

    // Create a queue, but don't obliterate an existing one!
    window.analytics = window.analytics || [];
    const segment: any = window.analytics;

    // If the real analytics.js is already on the page return.
    if (segment.initialize) {
      return;
    }

    // If the snippet was invoked already show an error.
    if (segment.invoked) {
      if (window.console && console.error) {
        console.error('Segment snippet included twice.');
      }
      return;
    }

    // Invoked flag, to make sure the snippet
    // is never invoked twice.
    segment.invoked = true;

    // A list of the methods in Analytics.js to stub.
    segment.methods = [
      'trackSubmit',
      'trackClick',
      'trackLink',
      'trackForm',
      'pageview',
      'identify',
      'reset',
      'group',
      'track',
      'ready',
      'alias',
      'debug',
      'page',
      'once',
      'off',
      'on',
      'addSourceMiddleware',
      'addIntegrationMiddleware',
      'setAnonymousId',
      'addDestinationMiddleware',
    ];

    // Define a factory to create stubs. These are placeholders
    // for methods in Analytics.js so that you never have to wait
    // for it to load to actually record data. The `method` is
    // stored as the first argument, so we can replay the data.
    segment.factory = (method: string) => (...params: any[]) => {
      const args = Array.prototype.slice.call(params);
      args.unshift(method);
      segment.push(args);
      return segment;
    };

    // For each of our methods, generate a queueing stub.
    for (let i = 0; i < segment.methods.length; i++) {
      const key = segment.methods[i];
      segment[key] = segment.factory(key);
    }

    // Define a method to load Analytics.js from our CDN,
    // and that will be sure to only ever load it once.
    segment.load = (key: string, options: any) => {
      // Create an async script element based on your key.
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://cdn.segment.com/analytics.js/v1/${key}/analytics.min.js`;

      // Insert our script next to the first script element.
      const first = document.getElementsByTagName('script')[0];
      first.parentNode?.insertBefore(script, first);
      segment._loadOptions = options;
    };

    // Add a version to keep track of what's in the wild.
    segment.SNIPPET_VERSION = '4.1.0';

    if (ENABLE_DEBUGGING) {
      segment?.debug(); // it does not work with a "true" parameter
    } else {
      segment?.debug(false);
    }

    // Load Analytics.js with your key, which will automatically
    // load the tools you've enabled for your account. Boosh!
    segment.load(API_KEY);
  };

  pageview = (name: string, pathname: string, extraFields?: Record<string, string>) => {
    if (!pathname) {
      throw new Error('[segment] pathname is required in .page()');
    }

    pathname = pathname.trim();
    if (pathname === '') {
      throw new Error('[segment] pathname cannot be an empty string in .page()');
    }

    if (pathname[0] !== '/') {
      pathname = `/${pathname}`;
    }

    // remove provider name from pathname
    const regex = /(\/provider)\/([A-z0-9%\-_]*)/g;
    const subst = '$1/#provider#';

    pathname = pathname.replace(regex, subst);

    analytics.page(name, {
      url: `${window.location.origin}${pathname}`,
      path: `${window.location.origin}${pathname}`,
      ...extraFields,
    });
  };

  track = (
    segmentName: SegmentName | string,
    properties?: Record<string, string | boolean | any[]>,
    options: Record<string, string> = {},
  ) => {
    analytics.track(segmentName, properties, options);
  };

  identify = (userId: string, traits: Record<string, string> = {}) => {
    if (this.lastUserId !== userId || !isEqual(this.lastUserTraits, traits)) {
      analytics.identify(userId, traits);
      this.lastUserId = userId;
      this.lastUserTraits = traits;
    }
  };

  group = (id: string) => {
    if (this.lastGroupId !== id) {
      analytics.group(id, {});
      this.lastGroupId = id;
    }
  };
}

export const Segment = new SegmentTracking(EnvConfig.SEGMENT_KEY);
